import type { OtpClientCreatedView, OtpClientListItem, OtpPlatformOtpTotalsView, WaInstance } from '@erp/shared';
import Decimal from 'decimal.js';
import { BookOpen, HandCoins, Inbox, KeySquare, Plus, Search, Send, ShieldCheck, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Button as ShButton,
  Skeleton,
} from '../../components/shadcn/index.js';
import {
  Button,
  CopyButton,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { formatDate } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

import {
  buildOtpClientPayload,
  DEFAULT_OTP_CLIENT_VALUES,
  OtpClientForm,
  type OtpClientFormValues,
  useOtpFormErrorMessage,
} from './_shared/OtpClientForm.js';
import { OtpKeyRevealDialog } from './_shared/OtpKeyRevealDialog.js';
import { errCode, fmtUsd, instanceStateVariant } from './_shared/OtpShared.js';

type StatusFilter = '' | 'active' | 'disabled';

/**
 * Platform console for the sellable "OTP as a Service" product — external
 * developers get an API key, send WhatsApp verification codes through a
 * gateway instance, and are billed per delivered message. This page is the
 * client roster; each row drills into usage + billing.
 */
export function PlatformOtpServicePage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const [items, setItems] = useState<OtpClientListItem[] | null>(null);
  const [instances, setInstances] = useState<WaInstance[] | null>(null);
  const [totals, setTotals] = useState<OtpPlatformOtpTotalsView | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  // Create dialog + the one-time key reveal that follows it.
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<OtpClientFormValues>({ ...DEFAULT_OTP_CLIENT_VALUES });
  const [created, setCreated] = useState<OtpClientCreatedView | null>(null);
  const formError = useOtpFormErrorMessage();

  const loadClients = useCallback(async (): Promise<void> => {
    try {
      const r = await platformApi().get<{ items: OtpClientListItem[] }>('/otp-service/clients');
      setItems(r.data.items);
    } catch {
      setItems([]);
      toast.error(t('otpService.loadError', { defaultValue: 'Could not load the OTP clients.' }));
    }
  }, [t]);

  // One-shot instance fetch — joined into the table for live delivery state.
  useEffect(() => {
    let cancelled = false;
    void platformApi()
      .get<{ items: WaInstance[] }>('/whatsapp/instances')
      .then((r) => { if (!cancelled) setInstances(r.data.items); })
      .catch(() => { if (!cancelled) setInstances([]); });
    return () => { cancelled = true; };
  }, []);

  // Lifetime accepted total + inbox badge — cheap enough for a one-shot fetch
  // rather than folding into loadClients(), and failure here shouldn't block
  // the client roster from rendering, so it fails silently to a '—' KPI.
  useEffect(() => {
    let cancelled = false;
    void platformApi()
      .get<OtpPlatformOtpTotalsView>('/otp-service/totals')
      .then((r) => { if (!cancelled) setTotals(r.data); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const hasActiveFilters = q.trim().length > 0 || statusFilter !== '';

  const filtered = useMemo(() => {
    if (!items) return null;
    const needle = q.trim().toLowerCase();
    return items.filter((c) => {
      if (statusFilter === 'active' && !c.isActive) return false;
      if (statusFilter === 'disabled' && c.isActive) return false;
      if (!needle) return true;
      return (
        c.name.toLowerCase().includes(needle) || c.keyPrefix.toLowerCase().includes(needle)
      );
    });
  }, [items, q, statusFilter]);

  const kpis = useMemo(() => {
    if (!items) return null;
    const active = items.filter((c) => c.isActive).length;
    const sentThisMonth = items.reduce((sum, c) => sum + c.stats.sentThisMonth, 0);
    // Outstanding = what customers still owe — negative balances are credit,
    // not a receivable, so only positive balances count.
    const outstanding = items.reduce(
      (sum, c) => {
        const bal = new Decimal(c.stats.balance || 0);
        return bal.gt(0) ? sum.plus(bal) : sum;
      },
      new Decimal(0),
    );
    return { active, sentThisMonth, outstanding };
  }, [items]);

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const built = buildOtpClientPayload(form);
    if (!built.ok) {
      toast.error(formError(built.error));
      return;
    }
    setCreating(true);
    try {
      const r = await platformApi().post<OtpClientCreatedView>('/otp-service/clients', built.payload);
      setCreateOpen(false);
      setForm({ ...DEFAULT_OTP_CLIENT_VALUES });
      setCreated(r.data);
      await loadClients();
    } catch (err) {
      const code = errCode(err);
      toast.error(code === 'conflict'
        ? t('otpService.nameTaken', { defaultValue: 'A client with that name already exists.' })
        : t('otpService.createError', { defaultValue: 'Could not create the client.' }));
    } finally {
      setCreating(false);
    }
  };

  const columns: DataTableColumn<OtpClientListItem>[] = [
    {
      id: 'name',
      header: t('otpService.table.name', { defaultValue: 'Name' }),
      sortValue: (row) => row.name,
      cellClassName: 'font-medium',
      cell: (row) => {
        const sub = row.company || row.contactEmail;
        return (
          <div className="flex flex-col">
            <Link
              to={`/platform/otp-service/${row.id}`}
              className="hover:text-brand hover:underline underline-offset-2"
            >
              {row.name}
            </Link>
            {sub && (
              <span
                className="truncate text-xs font-normal text-fg-muted"
                dir={row.company ? undefined : 'ltr'}
              >
                {sub}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'key',
      header: t('otpService.table.key', { defaultValue: 'Key' }),
      sortValue: (row) => row.keyPrefix,
      cell: (row) => (
        <div className="flex items-center gap-1">
          <code dir="ltr" className="font-mono text-xs text-fg-muted">{row.keyPrefix}…</code>
          <CopyButton value={row.keyPrefix} subject={t('otpService.table.key', { defaultValue: 'Key' })} />
        </div>
      ),
    },
    {
      id: 'instance',
      header: t('otpService.table.instance', { defaultValue: 'Instance' }),
      sortValue: (row) => row.instanceName ?? '',
      cell: (row) => {
        if (!row.instanceName) return <span className="text-xs text-fg-muted">—</span>;
        const inst = instances?.find((i) => i.name === row.instanceName);
        const missing = instances !== null && !inst;
        return (
          <div className="flex items-center gap-1.5">
            <span className="truncate font-mono text-xs">{row.instanceName}</span>
            <CopyButton value={row.instanceName} subject={t('otpService.table.instance', { defaultValue: 'Instance' })} />
            {missing ? (
              <Badge variant="danger">
                {t('otpService.instanceMissing', { defaultValue: 'Missing' })}
              </Badge>
            ) : inst ? (
              <Badge variant={instanceStateVariant(inst.state)}>
                {t(`whatsapp.state.${inst.state}`, { defaultValue: inst.state })}
              </Badge>
            ) : null}
          </div>
        );
      },
    },
    {
      id: 'price',
      header: t('otpService.table.price', { defaultValue: 'Price / msg' }),
      align: 'end',
      sortValue: (row) => Number(row.pricePerMessage),
      cell: (row) =>
        new Decimal(row.pricePerMessage || 0).isZero() ? (
          <Badge variant="secondary">{t('otpService.free', { defaultValue: 'Free' })}</Badge>
        ) : (
          <span dir="ltr" className="font-mono tabular-nums">{fmtUsd(row.pricePerMessage)}</span>
        ),
    },
    {
      id: 'sentThisMonth',
      header: t('otpService.table.sentThisMonth', { defaultValue: 'This month' }),
      align: 'end',
      sortValue: (row) => row.stats.sentThisMonth,
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => row.stats.sentThisMonth,
    },
    {
      id: 'balance',
      header: t('otpService.table.balance', { defaultValue: 'Balance' }),
      align: 'end',
      sortValue: (row) => Number(row.stats.balance),
      cell: (row) => {
        const bal = new Decimal(row.stats.balance || 0);
        const cls = bal.gt(0) ? 'text-danger' : bal.lt(0) ? 'text-success' : 'text-fg-muted';
        return (
          <span dir="ltr" className={`font-mono tabular-nums ${cls}`}>{fmtUsd(bal)}</span>
        );
      },
    },
    {
      id: 'status',
      header: t('otpService.table.status', { defaultValue: 'Status' }),
      sortValue: (row) => (row.isActive ? 0 : 1),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'secondary'}>
          {row.isActive
            ? t('otpService.statusActive', { defaultValue: 'Active' })
            : t('otpService.statusDisabled', { defaultValue: 'Disabled' })}
        </Badge>
      ),
    },
    {
      id: 'createdAt',
      header: t('otpService.table.createdAt', { defaultValue: 'Created' }),
      sortValue: (row) => row.createdAt,
      cellClassName: 'text-xs text-fg-muted',
      cell: (row) => formatDate(row.createdAt, { locale: i18n.language }),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning">
            <KeySquare className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {t('otpService.title', { defaultValue: 'OTP service' })}
            </h1>
            <p className="text-sm text-fg-muted">
              {t('otpService.subtitle', {
                defaultValue: 'WhatsApp OTP delivery for external developers — clients, keys, usage, and billing.',
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/platform/otp-service/payment-claims">
            <ShButton variant="outline">
              <Inbox className="h-4 w-4" />
              {t('otpService.paymentClaims.title', { defaultValue: 'Balance top-up requests' })}
              {totals !== null && totals.pendingClaimsCount > 0 && (
                <Badge variant="warning">{totals.pendingClaimsCount}</Badge>
              )}
            </ShButton>
          </Link>
          <Link to="/platform/otp-service/docs">
            <ShButton variant="outline">
              <BookOpen className="h-4 w-4" />
              {t('otpService.docsButton', { defaultValue: 'Documentation' })}
            </ShButton>
          </Link>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('otpService.newClient', { defaultValue: 'New client' })}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard
          tone="success"
          icon={HandCoins}
          label={t('otpService.kpi.totalAccepted', { defaultValue: 'Total accepted' })}
          value={totals ? fmtUsd(totals.totalAccepted) : '—'}
          loading={totals === null}
        />
        <KpiCard
          tone="brand"
          icon={KeySquare}
          label={t('otpService.kpi.clients', { defaultValue: 'Clients' })}
          value={items ? String(items.length) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="success"
          icon={ShieldCheck}
          label={t('otpService.kpi.active', { defaultValue: 'Active' })}
          value={kpis ? String(kpis.active) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="info"
          icon={Send}
          label={t('otpService.kpi.sentThisMonth', { defaultValue: 'Sent this month' })}
          value={kpis ? String(kpis.sentThisMonth) : '—'}
          loading={!items}
        />
        <KpiCard
          tone="warning"
          icon={Wallet}
          label={t('otpService.kpi.outstanding', { defaultValue: 'Outstanding' })}
          value={kpis ? fmtUsd(kpis.outstanding) : '—'}
          hint={kpis && kpis.outstanding.gt(0)
            ? t('otpService.kpi.owedByClients', { defaultValue: 'owed by clients' })
            : undefined}
          loading={!items}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[18rem] flex-1">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted"
              aria-hidden
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('otpService.search', { defaultValue: 'Search by name or key prefix' })}
              aria-label={t('otpService.search', { defaultValue: 'Search by name or key prefix' })}
              className="ps-9"
            />
          </div>
          <select
            className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label={t('otpService.statusFilter', { defaultValue: 'Status' })}
          >
            <option value="">{t('otpService.statusAll', { defaultValue: 'All' })}</option>
            <option value="active">{t('otpService.statusActive', { defaultValue: 'Active' })}</option>
            <option value="disabled">{t('otpService.statusDisabled', { defaultValue: 'Disabled' })}</option>
          </select>
        </div>
      </Card>

      {filtered === null ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : filtered.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            title={t('otpService.noResults', { defaultValue: 'No clients match these filters.' })}
            action={
              <ShButton variant="outline" size="sm" onClick={() => { setQ(''); setStatusFilter(''); }}>
                {t('otpService.clearFilters', { defaultValue: 'Clear filters' })}
              </ShButton>
            }
          />
        ) : (
          <EmptyState
            title={t('otpService.empty', { defaultValue: 'No OTP clients yet.' })}
            description={t('otpService.emptyHint', {
              defaultValue: 'Create the first client to issue its API key and start selling OTP delivery.',
            })}
            action={
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                {t('otpService.newClient', { defaultValue: 'New client' })}
              </Button>
            }
          />
        )
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={filtered}
            rowKey={(row) => row.id}
            rowClassName={(row) => (row.isActive ? undefined : 'opacity-60')}
            paginated={false}
            searchable={false}
          />
        </Card>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!creating) setCreateOpen(o); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('otpService.createTitle', { defaultValue: 'New OTP client' })}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void onCreate(e)} className="flex flex-col gap-4" noValidate>
            <OtpClientForm
              values={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              instances={instances}
              idPrefix="otp-create"
            />
            <DialogFooter>
              <ShButton type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
                {tc('actions.cancel', { defaultValue: 'Cancel' })}
              </ShButton>
              <Button type="submit" loading={creating}>
                {t('otpService.create', { defaultValue: 'Create' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <OtpKeyRevealDialog
        apiKey={created?.apiKey ?? null}
        clientName={created?.name ?? ''}
        onDone={() => setCreated(null)}
      />
    </div>
  );
}
